import { FullProduct } from "ts/api/product";
import Transfer from "ts/api/transfer";

import {
  IOrganiser,
  IShow,
  IStripeConnectAccountPublic,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { uniq } from "lodash";
import { BankTransferBillingDetails } from "store/exhibitors/cartStepper/slice";
import formatDate from "tools/formats/formatDate";
import { getReadableString } from "constraints/general";
import {
  getOrganiserTransfer,
  getSuppliersTransfers,
  OrganiserTransferMetadataContext,
} from "./calculateTransfers";
import type { TransferTax } from "./calculateTransfers";

type GetTransfersParams = {
  products: FullProduct[];
  organiser: IOrganiser;
  show: IShow;
  transferredPaymentDescription: string;
  orderId?: string;
  invoiceNumber?: string;
  billingMetadata?: BankTransferBillingDetails;
};

const getShowTransferTaxes = (show: IShow): TransferTax[] => {
  const showWithTaxCollections = show as IShow & {
    taxes?: TransferTax[];
    salesTaxes?: TransferTax[];
  };

  const taxCollections = [
    showWithTaxCollections.taxes,
    showWithTaxCollections.salesTaxes,
  ].filter((taxes): taxes is TransferTax[] => Array.isArray(taxes));

  const allTaxes = taxCollections
    .flat()
    .filter(
      (tax): tax is TransferTax =>
        !!tax &&
        typeof tax.name === "string" &&
        typeof tax.percentage === "number"
    );

  if (allTaxes.length) {
    return allTaxes;
  }

  return [
    {
      name: show.salesTaxName,
      percentage: show.salesTaxPercentage,
    },
  ];
};

const getTransfers = async ({
  products,
  organiser,
  show,
  transferredPaymentDescription,
  orderId,
  invoiceNumber,
  billingMetadata,
}: GetTransfersParams): Promise<Transfer[]> => {
  const uniqOwners = uniq(products.map((product) => product.owner.id));

  const suppliersStripeAccounts = await Promise.all(
    uniqOwners.map(async (productOwnerId) => {
      return {
        productOwnerId,
        supplierAccount: await apiV2.oldStripe.getSupplierCompanyStripeConnectAccount(
          {
            supplierCompanyId: productOwnerId,
          }
        ),
      };
    })
  );

  const validSupplierAccounts = suppliersStripeAccounts
    .map(({ supplierAccount }) => supplierAccount)
    .filter((acc) => !!acc?.accountId);

  if (validSupplierAccounts.length !== uniqOwners.length) {
    throw new Error("Not all suppliers have Stripe accounts");
  }

  const organiserCompanyId =
    organiser?.organiserCompanyToOrganisers?.[0]?.organiserCompanyId;

  if (!organiserCompanyId) {
    throw new Error("Organiser companyId not found");
  }

  const stripeAccount = await apiV2.oldStripe.getOrganiserCompanyStripeConnectAccountByCurrency(
    {
      currency: show.currency,
      organiserCompanyId,
    }
  );

  const stripeAccountId = stripeAccount?.accountId;
  if (!stripeAccountId) {
    throw new Error("Stripe account ids aren't set");
  }

  const suppliersStripeAccountsRecord: Record<
    string,
    IStripeConnectAccountPublic
  > = suppliersStripeAccounts.reduce<
    Record<string, IStripeConnectAccountPublic>
  >((acc, { supplierAccount, productOwnerId }) => {
    acc[productOwnerId] = supplierAccount;

    return acc;
  }, {});

  const baseTransferMetadata: OrganiserTransferMetadataContext = {
    showName:
      show.name.length > 50 ? `${show.name.substring(0, 47)}...` : show.name,
    showStartDate: formatDate(
      new Date(show.liveStart),
      `${getReadableString(show)}, HH:mm`
    ),
    ...(orderId && { orderId }),
  };

  const suppliersTransfers = getSuppliersTransfers(
    products,
    getShowTransferTaxes(show),
    transferredPaymentDescription,
    suppliersStripeAccountsRecord,
    {
      invoiceNumber,
      ...(billingMetadata && { billingDetails: billingMetadata }),
      ...baseTransferMetadata,
    }
  );
  const organiserTransfer = getOrganiserTransfer({
    organiser,
    suppliersTransfers,
    stripeAccountId,
    metadata: baseTransferMetadata,
  });
  const transfers: Transfer[] = [
    { ...organiserTransfer, transferredPaymentDescription },
    ...suppliersTransfers,
  ];
  return transfers;
};

export default getTransfers;
